import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import authService from '../../services/authService';
import AuthMessage from '../AuthMessage/AuthMessage';
import PasswordField from '../common/PasswordField/PasswordField';
import AuthPageLayout from '../AuthPageLayout/AuthPageLayout';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';
  
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordMatchError, setPasswordMatchError] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token');
    }
  }, [token]);

  const checkPasswordMatch = (confirmValue: string) => {
    if (confirmValue !== newPassword) {
      setPasswordMatchError('Passwords do not match!');
    } else {
      setPasswordMatchError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPasswordMatchError('');

    if (!token) {
      setError('Invalid or missing reset token');
      setLoading(false);
      return;
    }

    if (!newPassword.trim()) {
      setError('Please enter a new password');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMatchError('Passwords do not match!');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const response = await authService.resetPassword(token, newPassword);
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/signin');
        }, 2000);
      } else {
        setError(response.message || 'Invalid or expired token');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthPageLayout>
        <div className="reset-password-container">
          <div className="reset-password-card">
            <AuthMessage variant="error">
              Invalid or missing reset token. Please request a new password reset link.
            </AuthMessage>
            <Link to="/forgotPassword" className="btn-style-2">
              Request New Reset Link
            </Link>
          </div>
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <div className="reset-password-container">
        <div className="reset-password-card">
          <h2 className="reset-password-title">RESET PASSWORD</h2>
          
          {success ? (
            <AuthMessage variant="success">
              Password has been reset successfully. Redirecting to login page...
            </AuthMessage>
          ) : (
            <>
              {error && <AuthMessage variant="error">{error}</AuthMessage>}
              
              <form className="reset-password-form" onSubmit={handleSubmit}>
                <PasswordField
                  name="newPassword"
                  id="newPassword"
                  inputClassName="input-res"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                  minLength={6}
                />

                <PasswordField
                  name="confirmPassword"
                  id="confirmPassword"
                  inputClassName="input-res"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    checkPasswordMatch(e.target.value);
                  }}
                  disabled={loading}
                  required
                  minLength={6}
                />
                
                {passwordMatchError && (
                  <AuthMessage variant="error">{passwordMatchError}</AuthMessage>
                )}
                
                <button 
                  type="submit" 
                  className="btn-style-2"
                  disabled={loading || !!passwordMatchError}
                >
                  {loading ? 'RESETTING...' : 'RESET PASSWORD'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </AuthPageLayout>
  );
};

export default ResetPassword;

